import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Zap, BarChart3, ShieldCheck, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useEffect, useRef } from "react";

const PLANS = [
  { id: "free", name: "Free", price: 0, tokens: 50, features: ["50 tokens / month", "Full Prompt Builder", "History & filters", "Includes light in-app ads"], highlight: false, cta: "Start free" },
  { id: "pro", name: "Professional", price: 15, tokens: 500, features: ["500 tokens / month", "Everything in Free", "Ad-free experience", "Re-generate variations", "Priority support"], highlight: true, cta: "Go Professional" },
  { id: "business", name: "Business", price: 45, tokens: 2500, features: ["2500 tokens / month", "Everything in Professional", "Ad-free experience", "Team-ready", "Dedicated success manager"], highlight: false, cta: "Go Business" },
];

export default function Landing({ initialSection }) {
  const pricingRef = useRef(null);
  useEffect(() => {
    if (initialSection === "pricing" && pricingRef.current) {
      pricingRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [initialSection]);

  return (
    <div className="min-h-screen bg-[#F5F7FA] text-[#111827] overflow-x-hidden">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-[#F5F7FA]/80 backdrop-blur-lg border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2">
          <Link to="/" className="flex items-center shrink-0" data-testid="brand-logo">
            <img src="/promptyai-logo.png" alt="Promptyai.app" className="h-10 sm:h-12 w-auto object-contain" />
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-[#64748B]">
            <a href="#how" className="hover:text-[#111827]">How it works</a>
            <a href="#pricing" className="hover:text-[#111827]">Pricing</a>
            <a href="#faq" className="hover:text-[#111827]">FAQ</a>
          </nav>
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <Link to="/login"><Button variant="ghost" size="sm" data-testid="header-login-btn" className="px-2 sm:px-3">Sign in</Button></Link>
            <Link to="/register">
              <Button size="sm" className="bg-[#1F3A8A] hover:bg-[#1E40AF] text-white px-3 sm:px-4" data-testid="header-signup-btn">Start free</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative bg-grid overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-14 sm:pt-20 lg:pt-24 pb-20 sm:pb-28 lg:pb-32 grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-white border border-[#E2E8F0] text-xs font-semibold tracking-wider text-[#14B8A6] uppercase mb-6">
                <Zap className="w-3 h-3" /> Powered by Gemini Flash
              </div>
              <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-[1.05] text-[#111827]">
                Turn structured inputs into <span className="text-[#1F3A8A]">powerful</span> AI prompts.
              </h1>
              <p className="mt-5 sm:mt-6 text-base sm:text-lg text-[#64748B] max-w-2xl leading-relaxed">
                Get monthly tokens, build precise prompts from 5 required + 4 optional fields, and track results — all in one transparent workspace.
              </p>
              <div className="mt-8 sm:mt-10 flex flex-wrap gap-3">
                <Link to="/register">
                  <Button size="lg" className="bg-[#14B8A6] hover:bg-[#0D9488] text-white btn-lift gap-2" data-testid="hero-start-free-btn">
                    Start free <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                <a href="#pricing">
                  <Button size="lg" variant="outline" className="border-[#1F3A8A] text-[#1F3A8A] hover:bg-[#1F3A8A] hover:text-white" data-testid="hero-see-pricing-btn">
                    See pricing
                  </Button>
                </a>
              </div>
              <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 text-xs text-[#64748B]">
                <div className="flex items-center gap-2"><Check className="w-4 h-4 text-[#14B8A6]" /> 50 free tokens every month</div>
                <div className="flex items-center gap-2"><Check className="w-4 h-4 text-[#14B8A6]" /> No credit card required</div>
              </div>
            </motion.div>
          </div>

          {/* Hero visual: mock prompt card */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="lg:col-span-5"
          >
            <div className="relative mx-auto max-w-md lg:max-w-none pb-10">
              <div className="absolute -inset-4 bg-gradient-to-br from-[#14B8A6]/10 to-[#1F3A8A]/10 rounded-md blur-2xl"></div>
              <div className="relative bg-white border border-[#E2E8F0] rounded-md shadow-sm p-5 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#14B8A6]">Prompt Output</span>
                  <span className="font-mono text-xs text-[#64748B]">1 token</span>
                </div>
                <div className="space-y-3 font-mono text-xs sm:text-sm text-[#111827] leading-relaxed break-words">
                  <div><span className="text-[#1F3A8A] font-semibold">ROLE:</span> Senior product copywriter</div>
                  <div><span className="text-[#1F3A8A] font-semibold">TASK:</span> Craft a launch announcement for SaaS founders</div>
                  <div><span className="text-[#1F3A8A] font-semibold">STRUCTURE:</span> Hook → Problem → Solution → CTA</div>
                  <div><span className="text-[#1F3A8A] font-semibold">TONE:</span> Confident, concise, data-driven</div>
                </div>
                <div className="mt-6 pt-4 border-t border-[#E2E8F0] flex items-center justify-between">
                  <div className="flex gap-2">
                    <div className="px-2 py-1 rounded bg-[#F5F7FA] text-xs">Copy</div>
                    <div className="px-2 py-1 rounded bg-[#F5F7FA] text-xs">Save</div>
                  </div>
                  <div className="text-xs text-[#64748B]">Generated in 2.1s</div>
                </div>
              </div>
              {/* floating tokens widget */}
              <div className="absolute -bottom-4 left-2 sm:-bottom-6 sm:-left-6 bg-white border border-[#E2E8F0] rounded-md shadow-sm p-4 w-40 sm:w-44">
                <div className="text-xs font-bold uppercase tracking-wider text-[#64748B]">Tokens left</div>
                <div className="font-display text-3xl font-bold text-[#1F3A8A]">487</div>
                <div className="mt-2 h-1.5 bg-[#F5F7FA] rounded overflow-hidden">
                  <div className="h-full bg-[#14B8A6]" style={{ width: "78%" }}></div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="py-16 sm:py-20 lg:py-24 bg-white border-y border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-2xl mb-10 sm:mb-16">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#14B8A6] mb-3">How it works</div>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">Three steps to better prompts.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-4 sm:gap-6">
            {[
              { n: "01", title: "Sign up free", body: "Create an account in seconds. 50 free tokens every month — no card required." },
              { n: "02", title: "Fill 5 required fields", body: "AI tool, role, ask, context, structure — plus 4 optional refiners (methodology, clarity, keywords, audience)." },
              { n: "03", title: "Generate", body: "We craft a precise, production-ready prompt. 1 token per generation." },
            ].map((s) => (
              <div key={s.n} className="relative p-6 sm:p-8 bg-[#F5F7FA] border border-[#E2E8F0] rounded-md card-hover overflow-hidden">
                <div className="font-display text-6xl sm:text-7xl font-extrabold text-[#1F3A8A]/10 absolute top-4 right-6 leading-none">{s.n}</div>
                <div className="font-display text-xs font-bold tracking-widest text-[#14B8A6] mb-3">STEP {s.n}</div>
                <h3 className="font-display text-xl sm:text-2xl font-bold mb-2">{s.title}</h3>
                <p className="text-[#64748B] leading-relaxed text-sm sm:text-base">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" ref={pricingRef} className="py-16 sm:py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-2xl mx-auto mb-10 sm:mb-16">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#14B8A6] mb-3">Pricing</div>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">Token-based, no surprises.</h2>
            <p className="mt-4 text-[#64748B]">1 token = 1 prompt. Add top-ups anytime.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-4 sm:gap-6">
            {PLANS.map((p) => (
              <div
                key={p.id}
                data-testid={`pricing-card-${p.id}`}
                className={`relative p-6 sm:p-8 bg-white border rounded-md card-hover ${p.highlight ? "border-[#14B8A6] shadow-lg" : "border-[#E2E8F0]"}`}
              >
                {p.highlight && (
                  <div className="absolute -top-3 left-6 sm:left-8 px-3 py-1 bg-[#14B8A6] text-white text-xs font-bold uppercase tracking-wider rounded">Most popular</div>
                )}
                <h3 className="font-display text-2xl font-bold text-[#1F3A8A]">{p.name}</h3>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="font-display text-4xl sm:text-5xl font-extrabold">{p.price === 0 ? "Free" : `$${p.price}`}</span>
                  {p.price > 0 && <span className="text-[#64748B]">/mo</span>}
                </div>
                <div className="text-sm text-[#64748B] mt-1">{p.tokens} tokens / month</div>
                <ul className="mt-6 space-y-3 text-sm">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2"><Check className="w-4 h-4 text-[#14B8A6] mt-0.5 shrink-0" /> {f}</li>
                  ))}
                </ul>
                <Link to="/register" className="block mt-8">
                  <Button
                    className={`w-full ${p.highlight ? "bg-[#14B8A6] hover:bg-[#0D9488] text-white" : "bg-white border border-[#1F3A8A] text-[#1F3A8A] hover:bg-[#1F3A8A] hover:text-white"}`}
                    data-testid={`pricing-select-${p.id}`}
                  >
                    {p.cta || "Get started"}
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="py-14 sm:py-20 bg-[#111827] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid md:grid-cols-3 gap-4 sm:gap-8">
          {[
            { icon: ShieldCheck, title: "Secure by default", body: "Encryption in transit & at rest. Role-based access for teams." },
            { icon: BarChart3, title: "Built-in analytics", body: "Usage charts, top keywords, AI tools — all in one dashboard." },
            { icon: Zap, title: "Fast & predictable", body: "Transparent token counter. Never wonder what you'll be charged." },
          ].map((f) => (
            <div key={f.title} className="p-6 border border-white/10 rounded-md bg-grid-dark">
              <f.icon className="w-6 h-6 text-[#14B8A6] mb-4" strokeWidth={1.75} />
              <h3 className="font-display text-xl font-semibold mb-2">{f.title}</h3>
              <p className="text-white/60 text-sm leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-16 sm:py-20 lg:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-8 sm:mb-12">Questions, answered.</h2>
          <div className="space-y-4">
            {[
              { q: "How does token billing work?", a: "Each prompt generation consumes 1 token. Tokens reset at the start of each billing cycle. You can top up anytime if you run out." },
              { q: "Can I cancel anytime?", a: "Yes. Manage your subscription from the dashboard. You keep access until the end of the period." },
              { q: "Which AI tools are supported?", a: "Any — ChatGPT, Claude, Gemini, Perplexity, Midjourney. Specify the tool in the Prompt Builder, and we'll optimize for its strengths." },
              { q: "Which model builds my prompts?", a: "Every prompt is crafted by Google's Gemini Flash — the same lightning-fast reasoning model that powers Gemini's real-time features. You get expert-level output in seconds." },
            ].map((f) => (
              <details key={f.q} className="group p-5 sm:p-6 bg-white border border-[#E2E8F0] rounded-md cursor-pointer">
                <summary className="font-display font-semibold text-base sm:text-lg list-none flex items-center justify-between gap-3">
                  <span>{f.q}</span>
                  <span className="text-[#14B8A6] group-open:rotate-45 transition-transform shrink-0">+</span>
                </summary>
                <p className="mt-3 text-[#64748B] text-sm sm:text-base">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 sm:py-10 border-t border-[#E2E8F0] bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs sm:text-sm text-[#64748B]">
          <div className="flex items-center">
            <img src="/promptyai-logo.png" alt="Promptyai.app" className="h-8 sm:h-10 w-auto object-contain" />
          </div>
          <div className="text-center">© 2026 Promptyai.app — Built for prompt engineers.</div>
        </div>
      </footer>
    </div>
  );
}
